import AppIcon from '@shelflix/ui-web/components/AppIcon';
import React, { useEffect, useState } from 'react';
import shelfTvSvg from '@/assets/icons-normalized/shelf-tv.svg?raw';
import { DashboardCard } from '../DashboardCard';
import { Input } from '@shelflix/ui-web/components/ui/input';
import searchSvg from '@/assets/icons-normalized/search.svg?raw';
import { Table, TableHeader, TableHead, TableBody, TableRow, TableCell } from '@shelflix/ui-web/components/ui/table';
import arrowRightSvg from '@/assets/icons-normalized/arrow-right.svg?raw';
import { cn } from '../../../../../packages/ui-web/src/lib/utils';
import Loader from '@/components/Loader';

export interface MaterialCatalogItem {
  group: string;
  count: number;
  brand: string;
  extra: {
    activeCount: number;
    offlineCount: number;
    unavailableCount: number;
  };
}

export interface MaterialCatalogProps {
  materialCatalogData: {
    total: number;
    materialsCount: MaterialCatalogItem[];
  };
  search: string;
  onSearchChange: (value: string) => void;
  page?: number;
  onPageChange?: (page: number) => void;
  loading?: boolean;
  className?: string;
}

export function MaterialCatalog({
  materialCatalogData,
  search,
  onSearchChange,
  page,
  onPageChange,
  loading = false,
  className,
}: MaterialCatalogProps) {
  const [filteredData, setFilteredData] = useState<MaterialCatalogItem[]>(materialCatalogData.materialsCount);
  const currentPage = page || 1;
  const itemsPerPage = 10;

  useEffect(() => {
    if (search.trim() === '') {
      setFilteredData(materialCatalogData.materialsCount);
    } else {
      const lowerSearch = search.toLowerCase();
      setFilteredData(
        materialCatalogData.materialsCount.filter((item) => item.group.toLowerCase().includes(lowerSearch))
      );
    }
  }, [search, materialCatalogData]);

  // Calcular paginação
  const totalPages = Math.ceil(materialCatalogData.total / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, materialCatalogData.total);

  // Gerar array de páginas para exibir
  const getPageButtons = () => {
    const buttons: (number | string)[] = [];

    if (totalPages <= 5) {
      // Se tem 5 ou menos páginas, mostra todas
      for (let i = 1; i <= totalPages; i++) {
        buttons.push(i);
      }
    } else {
      // totalPages > 5
      const hasBefore = currentPage > 1;
      const hasAfter = currentPage < totalPages;

      if (hasBefore && hasAfter) {
        // Página atual tem espaço antes e depois
        // Manter página atual como segundo botão
        const firstPageInRange = Math.max(1, currentPage - 1);
        const lastPageInRange = Math.min(totalPages, firstPageInRange + 1);

        // Adicionar páginas do intervalo
        for (let i = firstPageInRange; i <= lastPageInRange; i++) {
          buttons.push(i);
        }

        // Adicionar fim se necessário
        if (lastPageInRange < totalPages) {
          buttons.push('...');
          buttons.push(totalPages);
        } else {
          buttons.push(totalPages);
        }
      } else if (!hasBefore) {
        // Estamos no começo (página 1)
        buttons.push(1, 2, 3, '...', totalPages);
      } else {
        // Estamos no fim
        buttons.push(1, '...', totalPages - 2, totalPages - 1, totalPages);
      }
    }

    return buttons.slice(0, 5); // Garantir máximo 5 elementos
  };

  // Funções de navegação
  const handlePageChange = (pageNumber: number) => {
    if (pageNumber >= 1 && pageNumber <= totalPages && onPageChange) {
      onPageChange(pageNumber);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages && onPageChange) {
      onPageChange(currentPage + 1);
    }
  };

  return (
    <DashboardCard title="Catálogo de Materiais" icon={shelfTvSvg} className={cn('bg-[#0E0E0E]', className)}>
      <Input
        placeholder="Filtrar por nome de material"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        prependIcon={
          <AppIcon
            source="svg"
            name="search"
            svgContent={searchSvg}
            size={20}
            weight={400}
            colorStyle={{ type: 'solid', color: '#B2A598' }}
          />
        }
        className="border-[#0E0E0E] bg-[#191919] text-sm text-[#B2A598] placeholder:text-[#B2A598]"
      />
      {loading ? (
        <Loader message="Carregando catálogo de materiais..." />
      ) : (
        <>
          <Table className="table-fixed">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40%] font-normal text-[#B2A598]">Material</TableHead>
                <TableHead className="w-[12%] font-normal text-[#B2A598]">Marca</TableHead>
                <TableHead className="w-[12%] font-normal text-[#B2A598]">Total</TableHead>
                <TableHead className="w-[12%] font-normal text-[#B2A598]">Online</TableHead>
                <TableHead className="w-[12%] font-normal text-[#B2A598]">Offline</TableHead>
                <TableHead className="w-[12%] font-normal text-[#B2A598]">Indisp.</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredData.map((item, index) => (
                <TableRow key={index}>
                  <TableCell className="w-[40%] truncate text-[12px] text-[#F2E6DA]">{item.group}</TableCell>
                  <TableCell className="w-[12%] truncate text-[12px] text-[#F2E6DA]">{item.brand}</TableCell>
                  <TableCell className="w-[12%] truncate text-[12px] text-[#F2E6DA]">{item.count}</TableCell>
                  <TableCell className="w-[12%] truncate text-[12px] text-[#85CC85]">
                    {item.extra.activeCount}
                  </TableCell>
                  <TableCell className="w-[12%] truncate text-[12px] text-[#CC4747]">
                    {item.extra.offlineCount}
                  </TableCell>
                  <TableCell className="w-[12%] truncate text-[12px] text-[#33AEFF]">
                    {item.extra.unavailableCount}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
      <div className="mt-auto flex w-full items-center gap-4 px-4">
        <span className="mr-auto text-xs text-[#B4B4B4]">
          Mostrando {totalPages === 0 ? 0 : startIndex + 1}-{endIndex} de {materialCatalogData.total} materiais
        </span>
        <div className="flex gap-2.5">
          {getPageButtons().map((pageNum, index) =>
            pageNum === '...' ? (
              <div
                key={`ellipsis-${index}`}
                className="flex h-8 w-8 items-center justify-center text-xs text-[#736A61]"
              >
                ...
              </div>
            ) : (
              <button
                key={pageNum}
                onClick={() => handlePageChange(pageNum as number)}
                disabled={totalPages === 0}
                className={cn(
                  'h-8 w-8 rounded-[5px] border text-xs transition-colors',
                  currentPage === pageNum
                    ? 'bg-primary border-primary text-white'
                    : 'border-[#736A61] bg-[#191919] text-[#736A61] hover:border-[#B2A598] hover:text-[#B2A598]'
                )}
              >
                {pageNum}
              </button>
            )
          )}
        </div>
        <button
          onClick={handleNextPage}
          disabled={currentPage >= totalPages || totalPages === 0}
          className="flex h-8 items-center gap-1 rounded-[5px] bg-[#191919] px-2 text-xs text-[#F2E6DA] hover:bg-[#282828] disabled:cursor-not-allowed disabled:opacity-50"
        >
          Proximo
          <AppIcon
            source="svg"
            name="right-arrow"
            svgContent={arrowRightSvg}
            size={16}
            weight={900}
            colorStyle={{ type: 'solid', color: '#F2E6DA' }}
          />
        </button>
      </div>
    </DashboardCard>
  );
}
